import React, { FC, useMemo } from "react";
import { LANGUAGE } from "_constants";
import { dedupeByKey, fmt, isNum, safeNum } from "./utils";

type Props = {
  status: any;
  language?: LANGUAGE;
};

type Row = {
  rank?: number; // i  (Sıra)
  team: string; // n
  played?: number; // v1 (Oynanan maç sayısı)
  gd?: number; // v2 (Averaj)
  pts?: number; // v3 (Puan)
};

const Standings: FC<Props> = ({ status }) => {
  const rawT1 =
    (status as any)?.T1?.n ??
    (status as any)?.t1?.n ??
    (status as any)?.T1n ??
    (status as any)?.t1n ??
    "";

  const rawT2 =
    (status as any)?.T2?.n ??
    (status as any)?.t2?.n ??
    (status as any)?.T2n ??
    (status as any)?.t2n ??
    "";

  const norm = (s: unknown) =>
    String(s ?? "")
      .trim()
      .toLowerCase();

  const nT1 = norm(rawT1);
  const nT2 = norm(rawT2);

  const rows = useMemo<Row[]>(() => {
    const lt = (status as any)?.lt || {};
    const list = Object.values(lt ?? {}) as any[];
    const mapped: Row[] = list.map((t: any) => ({
      rank: safeNum(t?.i),
      team: String(t?.n ?? ""),
      played: safeNum(t?.v1),
      gd: safeNum(t?.v2),
      pts: safeNum(t?.v3),
    }));

    const final = mapped;

    final.sort((a, b) => {
      if (isNum(a.rank) && isNum(b.rank)) return a.rank! - b.rank!;
      if (isNum(a.pts) !== isNum(b.pts)) return (b.pts ?? 0) - (a.pts ?? 0);
      if (isNum(a.gd) !== isNum(b.gd)) return (b.gd ?? 0) - (a.gd ?? 0);
      if (isNum(a.played) !== isNum(b.played))
        return (a.played ?? 0) - (b.played ?? 0);
      return a.team.localeCompare(b.team);
    });

    return dedupeByKey(final, (r) => `${r.team}|${r.played}|${r.gd}|${r.pts}`);
  }, [status]);

  if (!rows.length) return null;

  const gridTemplate =
    "minmax(44px,4vw) minmax(120px,2fr) minmax(52px,0.7fr) minmax(52px,0.7fr) minmax(56px,0.8fr)";

  return (
    <div
      role="table"
      aria-label="Standings"
      className="w-full overflow-hidden rounded-[1vw] border border-border text-lighterGray"
    >
      <div role="rowgroup" className="border-b border-border bg-darkestBlue">
        <div
          role="row"
          className="grid gap-2 px-[4vw] py-[3vw] text-[3vw] font-bold text-center text-statisticTitle"
          style={{
            gridTemplateColumns: gridTemplate,
          }}
        >
          <div role="columnheader" />
          <div role="columnheader" />
          <div role="columnheader">P</div>
          <div role="columnheader">+/-</div>
          <div role="columnheader">Pts</div>
        </div>
      </div>
      <div role="rowgroup">
        {rows.map((r, idx) => {
          const isHighlight = norm(r.team) === nT1 || norm(r.team) === nT2;
          return (
            <div
              role="row"
              key={`${r.team}-${idx}`}
              className={`grid gap-2 px-[4vw] py-[4vw] text-[3vw] text-center border-b border-border ${
                isHighlight
                  ? "font-black text-white"
                  : "font-medium text-lightGray"
              }`}
              style={{
                gridTemplateColumns: gridTemplate,
              }}
            >
              <div role="cell">{r.rank ?? "-"}</div>
              <div role="cell" title={r.team} className="truncate text-left">
                {r.team}
              </div>
              <div role="cell">{fmt(r.played)}</div>
              <div role="cell">{fmt(r.gd)}</div>
              <div role="cell">{fmt(r.pts)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Standings;
